// =============================================================================
// PROJECT FORM - MODAL COMPONENT
// =============================================================================
/**
 * ProjectForm Component
 * 
 * Modal form for creating and editing projects
 * 
 * @component
 * @version 2.0 - Refactored with constants module
 */

import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { countries } from '../../utils/countries';
import InfoModal from '../ui/modals/InfoModal';
import { useClientsList } from '../../hooks/useConfigQueries';

// Constants
import { 
    INITIAL_FORM_STATE, 
    DEFAULT_PROJECT_STATUS,
    VALIDATION_MESSAGES,
    FORM_CONFIG,
    MODAL_TITLES,
    BUTTON_LABELS
} from './ProjectForm.constants';

// =============================================================================
// MAIN COMPONENT
// =============================================================================
const ProjectForm = ({ show, onHide, onSave, projectToEdit }) => {
    const { data: clients = [] } = useClientsList({ enabled: show });
    const [formData, setFormData] = useState(INITIAL_FORM_STATE);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [infoModalMessage, setInfoModalMessage] = useState('');

    // ========================================================================
    // EFFECTS
    // ========================================================================
    useEffect(() => {
        if (show) {
            // Reset or populate form data
            if (projectToEdit) {
                setFormData({
                    project_name: projectToEdit.project_name || '',
                    client_id: projectToEdit.client_id || '',
                    crew_number: projectToEdit.crew_number || '',
                    country: projectToEdit.country || ''
                });
            } else {
                setFormData(INITIAL_FORM_STATE);
            }
        }
    }, [show, projectToEdit]);

    // ========================================================================
    // HANDLERS
    // ========================================================================
    
    /**
     * Handle form field changes
     */
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    /**
     * Handle crew number input (restricted to 2 digits)
     */
    const handleCrewNumberChange = (e) => {
        const val = e.target.value.replace(/[^0-9]/g, '').slice(0, FORM_CONFIG.crewNumber.maxDigits);
        handleChange({ target: { name: 'crew_number', value: val } });
    };

    /**
     * Validate and submit form
     */
    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Validate required fields
        if (
            !formData.project_name.trim()
            || !formData.client_id
            || !formData.country
            || !formData.crew_number?.trim()
        ) {
            setInfoModalMessage(VALIDATION_MESSAGES.REQUIRED_FIELDS);
            setShowInfoModal(true);
            return;
        }

        // Prepare data for backend
        const dataToSend = {
            ...formData,
            client_id: parseInt(formData.client_id, 10),
            status: DEFAULT_PROJECT_STATUS
        };

        onSave(dataToSend, projectToEdit ? projectToEdit.project_id : null);
    };

    // ========================================================================
    // RENDER
    // ========================================================================
    const isEditMode = Boolean(projectToEdit);
    
    return (
        <>
            <Modal show={show} onHide={onHide} size="lg" centered contentClassName="border" style={{ '--bs-modal-border-color': 'rgba(128, 128, 128, 0.5)' }}>
                <Modal.Header closeButton style={{ backgroundColor: 'var(--archon-navy)', borderBottom: 'none' }}>
                    <Modal.Title style={{ color: 'var(--archon-white)', fontFamily: 'Century Gothic, sans-serif' }}>
                        {isEditMode ? MODAL_TITLES.EDIT : MODAL_TITLES.CREATE}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="small-text-card">
                    <Form onSubmit={handleSubmit}>
                        {/* Row 1: Project Name & Country */}
                        <Row>
                            <Col md={8}>
                                <Form.Group className="mb-3" controlId="projectName">
                                    <Form.Label>
                                        {FORM_CONFIG.projectName.label}
                                        {FORM_CONFIG.projectName.required && <span className="text-danger"> *</span>}
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder={FORM_CONFIG.projectName.placeholder}
                                        name="project_name"
                                        value={formData.project_name}
                                        onChange={handleChange}
                                        required={FORM_CONFIG.projectName.required}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3" controlId="country">
                                    <Form.Label>
                                        {FORM_CONFIG.country.label}
                                        {FORM_CONFIG.country.required && <span className="text-danger"> *</span>}
                                    </Form.Label>
                                    <Form.Select
                                        aria-label={FORM_CONFIG.country.placeholder}
                                        name="country"
                                        value={formData.country}
                                        onChange={handleChange}
                                        required={FORM_CONFIG.country.required}
                                    >
                                        <option value="">{FORM_CONFIG.country.placeholder}</option>
                                        {countries.map(c => (
                                            <option key={c.code} value={c.name}>{c.name}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Row 2: Client & Crew Number */}
                        <Row>
                            <Col md={8}>
                                <Form.Group className="mb-3" controlId="client">
                                    <Form.Label>
                                        {FORM_CONFIG.client.label}
                                        {FORM_CONFIG.client.required && <span className="text-danger"> *</span>}
                                    </Form.Label>
                                    <Form.Select
                                        aria-label={FORM_CONFIG.client.placeholder}
                                        name="client_id"
                                        value={formData.client_id}
                                        onChange={handleChange}
                                        required={FORM_CONFIG.client.required}
                                    >
                                        <option value="">{FORM_CONFIG.client.placeholder}</option>
                                        {clients.map(client => (
                                            <option key={client.client_id} value={client.client_id}>
                                                {client.client_name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3" controlId="crewNumber">
                                    <Form.Label>{FORM_CONFIG.crewNumber.label}</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="crew_number"
                                        value={formData.crew_number || ''}
                                        onChange={handleCrewNumberChange}
                                        min={0}
                                        max={99}
                                        placeholder={FORM_CONFIG.crewNumber.placeholder}
                                        required={FORM_CONFIG.crewNumber.required}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={handleSubmit}>
                        {BUTTON_LABELS.SAVE}
                    </Button>
                    <Button variant="secondary" onClick={onHide}>
                        {BUTTON_LABELS.CLOSE}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Validation Error Modal */}
            <InfoModal
                show={showInfoModal}
                onHide={() => setShowInfoModal(false)}
                title="Validation Error"
                body={infoModalMessage}
            />
        </>
    );
};

export default ProjectForm;
